import { useState, type CSSProperties, type ReactNode } from 'react';
import { ErreurApi } from '../../../api/client.js';
import { CIBLE_MIN, COULEURS, styleBouton, styleBoutonPrincipal } from '../../../app/styles.js';
import { useRoleCourant } from '../../../app/role-courant.js';
import { AvertissementForcage } from '../../demarrage/forcage.js';
import { CATALOGUE_DEMARRAGE } from '../../demarrage/catalogue.js';
import { CONDITION_MANDAT } from '../../genealogie/actions.js';
import type { ApercuImport, IssueLigne, LigneApercu, RapportImport, SourceImportContacts } from './source.js';

/**
 * Onglet « Import de contacts » du dossier (PARITE-5) : disposition de la démo (brigitte/ImportContacts) branchée sur l'API réelle.
 * 1. Choisir le fichier (lu sur l'appareil) ; 2. Vérifier l'aperçu : nombre de lignes, premières lignes, doublons écartés,
 * erreurs à corriger ; rien n'est enregistré ; 3. Confirmer explicitement : les personnes sont ajoutées au dossier, aucun
 * message ne part. Le droit d'importer vient des permissions de la session ; le serveur refuse de toute façon (403).
 */
export interface PropsVueImportContacts {
  source: SourceImportContacts;
  peutImporter: boolean;
}

const RAISON = 'Votre rôle ne permet pas d’importer des contacts.';
const MESSAGE_CONFLIT =
  'La liste a changé depuis l’aperçu (une personne a pu être ajoutée entre-temps) : relancez l’aperçu avant d’importer.';

const pluriel = (n: number, un: string, plusieurs: string): string => `${n} ${n > 1 ? plusieurs : un}`;
const ISSUES: Readonly<Record<IssueLigne, [string, string, string]>> = {
  a_importer: ['À importer', '#E4F3EC', '#1F5E43'],
  doublon: ['Doublon', COULEURS.ambreFond, COULEURS.ambre],
  erreur: ['Erreur', '#FDECEA', COULEURS.rouge],
};

/** Message lisible d'une erreur ; pour un fichier refusé, le détail du serveur (équipe seulement) dit quoi corriger. */
function messageDe(e: unknown): string {
  if (e instanceof ErreurApi) {
    if (e.code === 'VERSION_CONFLICT') return MESSAGE_CONFLIT;
    if (e.code === 'VALIDATION_FAILED' && e.detail) return e.detail;
    return e.messageUtilisateur;
  }
  return e instanceof Error ? e.message : 'Une erreur est survenue. Réessayez.';
}

const carte: CSSProperties = {
  background: COULEURS.blanc,
  border: '1px solid #E2E1EA',
  borderRadius: 12,
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  minWidth: 0,
};
const titre: CSSProperties = { margin: 0, fontSize: 18, color: COULEURS.marine };
const discret: CSSProperties = { color: COULEURS.ardoise, fontSize: 13, margin: 0 };
const badgeEquipe: CSSProperties = {
  alignSelf: 'flex-start',
  fontSize: 12,
  fontWeight: 600,
  background: '#EEF0F8',
  color: COULEURS.marine,
  borderRadius: 999,
  padding: '2px 10px',
};
const alerte: CSSProperties = { margin: 0, color: COULEURS.rouge, fontWeight: 600 };
const actions: CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 8 };

function Carte({ libelle, titreCarte, children }: { libelle?: string; titreCarte: string; children: ReactNode }) {
  return (
    <section aria-label={libelle ?? titreCarte} style={carte}>
      <h2 style={titre}>{titreCarte}</h2>
      {children}
    </section>
  );
}

function Pastille({ issue }: { issue: IssueLigne }) {
  const [libelle, fond, encre] = ISSUES[issue];
  return (
    <span
      style={{
        display: 'inline-block',
        fontSize: 12,
        fontWeight: 600,
        background: fond,
        color: encre,
        borderRadius: 999,
        padding: '2px 10px',
      }}
    >
      {libelle}
    </span>
  );
}

function TableauApercu({ lignes }: { lignes: LigneApercu[] }) {
  const cellule: CSSProperties = { padding: '8px 6px', borderTop: '1px solid #EEEDF3', textAlign: 'left', verticalAlign: 'top' };
  return (
    <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
      <table aria-label="Premières lignes du fichier" style={{ borderCollapse: 'collapse', width: '100%', fontSize: 14 }}>
        <thead>
          <tr>
            {['Ligne', 'Personne et coordonnées', 'Issue'].map((t) => (
              <th key={t} scope="col" style={{ ...cellule, borderTop: 'none', color: COULEURS.ardoise, fontWeight: 600 }}>
                {t}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lignes.map((l) => (
            <tr key={l.line}>
              <td style={cellule}>{l.line}</td>
              <td style={{ ...cellule, overflowWrap: 'anywhere' }}>
                {`${l.firstNames} ${l.lastName}`.trim() || '—'}
                {l.relationLabel ? <span style={discret}>{` · ${l.relationLabel}`}</span> : null}
                <span style={{ ...discret, display: 'block' }}>{[l.phone, l.email].filter(Boolean).join(' · ') || 'sans coordonnée'}</span>
              </td>
              <td style={{ ...cellule, minWidth: 110 }}>
                <Pastille issue={l.outcome} />
                {l.reason ? <span style={{ ...discret, display: 'block' }}>{l.reason}</span> : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ListeIssues({ libelle, issues }: { libelle: string; issues: { line: number; reason: string }[] }) {
  if (issues.length === 0) return null;
  return (
    <div aria-label={libelle} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <strong style={{ fontSize: 14 }}>{libelle}</strong>
      <ul style={{ margin: 0, paddingLeft: 20, fontSize: 14 }}>
        {issues.map((i) => (
          <li key={`${i.line}-${i.reason}`}>{`Ligne ${i.line} : ${i.reason}`}</li>
        ))}
      </ul>
    </div>
  );
}

export function VueImportContacts({ source, peutImporter }: PropsVueImportContacts) {
  const [fichier, setFichier] = useState<{ csv: string; nom: string } | null>(null);
  const [apercu, setApercu] = useState<ApercuImport | null>(null);
  const [rapport, setRapport] = useState<RapportImport | null>(null);
  const [erreur, setErreur] = useState<{ message: string; conflit: boolean } | null>(null);
  const [confirmation, setConfirmation] = useState(false);
  const [enCours, setEnCours] = useState(false);
  const [avertissement, setAvertissement] = useState(false);
  const role = useRoleCourant();

  async function lireApercu(csv: string, nom: string) {
    setEnCours(true);
    setErreur(null);
    setApercu(null);
    setRapport(null);
    try {
      setApercu(await source.apercu(csv, nom));
    } catch (e) {
      setErreur({ message: messageDe(e), conflit: false });
    } finally {
      setEnCours(false);
    }
  }

  async function choisir(liste: FileList | null) {
    const f = liste?.[0];
    if (!f) return;
    const csv = await f.text();
    setFichier({ csv, nom: f.name });
    await lireApercu(csv, f.name);
  }

  async function importer() {
    if (!fichier || !apercu) return;
    setEnCours(true);
    setErreur(null);
    try {
      setRapport(await source.importer(fichier.csv, fichier.nom, apercu.fingerprint, apercu.importableCount));
      setConfirmation(false);
      setApercu(null);
    } catch (e) {
      setConfirmation(false);
      setErreur({ message: messageDe(e), conflit: e instanceof ErreurApi && e.code === 'VERSION_CONFLICT' });
    } finally {
      setEnCours(false);
    }
  }

  const n = apercu?.importableCount ?? 0;
  const libelleImporter = n === 0 ? 'Rien à importer' : `Importer ${n > 1 ? `les ${n} contacts` : 'le contact'}`;
  // Jamais bloqué (décision de Joel du 30/10) : comme l'arbre, un forçage du mandat par la dirigeante lève le blocage ; sans lui,
  // « Importer » ouvre l'avertissement (forçage sur place pour la dirigeante, « Seule Isabelle peut débloquer » pour l'assistante).
  const mandatSigne = apercu?.mandateSigned === true;
  const mandatForce = !mandatSigne && Boolean(apercu?.mandateForcing);
  const mandatOk = mandatSigne || mandatForce;
  const raisonImport = n === 0
      ? 'Aucune ligne à importer : tout est doublon ou erreur.'
      : undefined;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
      <Carte titreCarte="Importer des contacts">
        <span style={badgeEquipe}>visible : équipe MAAT</span>
        <p style={discret}>
          Fichier CSV, séparateur « ; » ou « , ». Colonnes : prénom et nom (obligatoires), téléphone, e-mail, vivant (oui, non ou inconnu),
          canal, lien, rôle. Rien n’est enregistré avant votre confirmation ; aucun message n’est envoyé.
        </p>
        <label htmlFor="import-fichier" style={{ fontWeight: 600, fontSize: 14 }}>
          1. Choisir le fichier CSV
        </label>
        <input
          id="import-fichier"
          type="file"
          accept=".csv,text/csv"
          disabled={!peutImporter || enCours}
          onChange={(e) => void choisir(e.target.files)}
          style={{ minHeight: CIBLE_MIN, maxWidth: '100%' }}
        />
        {!peutImporter ? <p style={discret}>{RAISON}</p> : null}
        {enCours && !confirmation ? <p role="status">Lecture du fichier…</p> : null}
        {erreur ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
            <p role="alert" style={alerte}>
              {erreur.message}
            </p>
            {erreur.conflit && fichier ? (
              <button type="button" style={styleBouton} onClick={() => void lireApercu(fichier.csv, fichier.nom)}>
                Relancer l’aperçu
              </button>
            ) : null}
          </div>
        ) : null}
      </Carte>

      {apercu ? (
        <Carte libelle="Aperçu de l’import" titreCarte="2. Vérifier l’aperçu">
          <p style={{ margin: 0 }}>
            <strong>
              {`${pluriel(apercu.totalRows, 'ligne', 'lignes')} dans le fichier : ${pluriel(n, 'contact à importer', 'contacts à importer')}, ${pluriel(apercu.duplicateCount, 'doublon écarté', 'doublons écartés')}, ${pluriel(apercu.errorCount, 'erreur à corriger', 'erreurs à corriger')}.`}
            </strong>
          </p>
          {apercu.preview.length > 0 ? <p style={discret}>{`Les ${apercu.preview.length} premières lignes du fichier :`}</p> : null}
          <TableauApercu lignes={apercu.preview} />
          <ListeIssues libelle="Doublons écartés" issues={apercu.duplicates} />
          <ListeIssues libelle="Erreurs à corriger" issues={apercu.errors} />
          {apercu.columnsIgnored.length > 0 ? <p style={discret}>{`Colonnes ignorées : ${apercu.columnsIgnored.join(', ')}.`}</p> : null}
          {mandatForce ? (
            <p data-mandat-force="" style={{ ...discret, color: COULEURS.ambre }}>
              Mandat levé par un forçage de {CATALOGUE_DEMARRAGE.intervenants.dirigeante} : l’import est tracé avec ce forçage.
            </p>
          ) : null}
          {!mandatOk ? (
            <p role="alert" style={alerte}>
              Le mandat de recherche n’est pas signé : nous vous déconseillons d’ajouter des personnes à ce dossier (loi 2006-728, art. 36).
            </p>
          ) : null}
          <div style={actions}>
            <button
              type="button"
              style={styleBoutonPrincipal}
              disabled={!peutImporter || enCours || raisonImport !== undefined}
              title={raisonImport ?? (peutImporter ? undefined : RAISON)}
              onClick={() => (mandatOk ? setConfirmation(true) : setAvertissement(true))}
            >
              {libelleImporter}
            </button>
          </div>
          {avertissement ? (
            <AvertissementForcage
              conditions={[CONDITION_MANDAT]}
              role={role}
              dirigeante={CATALOGUE_DEMARRAGE.intervenants.dirigeante}
              annuler={() => setAvertissement(false)}
              forcer={async (motif) => {
                if (!source.forcerMandat || !(await source.forcerMandat(motif))) return false;
                setAvertissement(false);
                // L'aperçu relu porte le forçage : « Importer » mène alors à la confirmation habituelle.
                if (fichier) await lireApercu(fichier.csv, fichier.nom);
                return true;
              }}
              prevenir={async () => (source.prevenirDirigeante ? source.prevenirDirigeante() : false)}
            />
          ) : null}
          {confirmation ? (
            <div
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="import-confirmation-titre"
              style={{ ...carte, border: `2px solid ${COULEURS.boutonPrincipal}` }}
            >
              <h3 id="import-confirmation-titre" style={{ margin: 0, fontSize: 16, color: COULEURS.marine }}>
                Confirmer l’import ?
              </h3>
              <p style={{ margin: 0 }}>
                {`${pluriel(n, 'contact sera ajouté', 'contacts seront ajoutés')} à ce dossier, avec leurs coordonnées réservées à l’équipe. Les doublons et les erreurs ne sont pas importés. Aucun message ne sera envoyé.`}
              </p>
              <div style={actions}>
                <button type="button" style={styleBoutonPrincipal} disabled={enCours} onClick={() => void importer()}>
                  Confirmer l’import
                </button>
                <button type="button" style={styleBouton} disabled={enCours} onClick={() => setConfirmation(false)}>
                  Annuler
                </button>
              </div>
            </div>
          ) : null}
        </Carte>
      ) : null}

      {rapport ? (
        <Carte libelle="Compte rendu de l’import" titreCarte="Import terminé">
          <p role="status" style={{ margin: 0 }}>
            {rapport.createdCount > 0
              ? `${pluriel(rapport.createdCount, 'personne ajoutée', 'personnes ajoutées')} au dossier : ${rapport.persons.map((p) => p.displayName).join(', ')}.`
              : 'Aucune personne ajoutée.'}
          </p>
          <p
            style={{ margin: 0 }}
          >{`${pluriel(rapport.duplicateCount, 'doublon', 'doublons')} et ${pluriel(rapport.errorCount, 'erreur', 'erreurs')} écartés.`}</p>
          <p style={discret}>Aucun message n’a été envoyé.</p>
        </Carte>
      ) : null}
    </div>
  );
}
